import { CardNumberElement } from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import {
  Address,
  BankFormIsValid,
  CardFormIsValid,
  CreateStipePaymentMethodParams,
  OnFormIsValidOptions,
  PaymentMethodType,
  OnFormIsValidType,
  Payment,
  SavedMethodFormIsValid,
  BankPaymentInformation,
  CardPaymentInformation,
  SavedPaymentInformation,
} from './interfaces';

export const createStripePaymentMethod = async ({
  stripe,
  stripeElements,
  cardName,
  address,
}: CreateStipePaymentMethodParams) => {
  const { error, paymentMethod } = await stripe.createPaymentMethod({
    type: 'card',
    card: stripeElements.getElement(CardNumberElement) as StripeCardNumberElement,
    billing_details: {
      name: cardName,
      address: {
        line1: address.line1,
        line2: address.line2,
        postal_code: address.postalCode,
        city: address.city,
        country: address.country,
        state: address.state,
      },
    },
  });
  return { error, paymentMethod };
};

/**
 * Extracts the address from the formData into its own object
 * @param formData the data to extract the address from
 * @returns the address as expected by our backend
 */
const extractAddress = (formData: CardFormIsValid | BankFormIsValid): Address => {
  const { address1, address2, zipCode, city, state, country } = formData;
  return {
    line1: address1,
    line2: address2,
    postalCode: zipCode.toString(),
    city,
    state,
    country,
  };
};

const processNewCard = async (formData: CardFormIsValid): Promise<Payment> => {
  const { stripe, stripeElements, cardName } = formData;

  const address = extractAddress(formData);

  const { error, paymentMethod } = await createStripePaymentMethod({
    stripe,
    stripeElements,
    cardName,
    address,
  });

  if (error || !paymentMethod) {
    return Promise.resolve({ error });
  }

  const paymentInformation: CardPaymentInformation = {
    paymentMethodType: PaymentMethodType.CARD,
    paymentMethodId: paymentMethod.id,
    address,
  };

  return { paymentInformation };
};

const processNewBank = async (formData: BankFormIsValid): Promise<Payment> => {
  const address = extractAddress(formData);

  const paymentInformation: BankPaymentInformation = {
    paymentMethodType: PaymentMethodType.BANK_ACCOUNT,
    plaidAccountId: formData.plaidAccountId,
    plaidPublicToken: formData.plaidToken,
    address,
  };

  return Promise.resolve({
    paymentInformation,
  });
};

const processSavedPayment = (formData: SavedMethodFormIsValid): Promise<Payment> => {
  const paymentInformation: SavedPaymentInformation = {
    paymentMethodType: formData.paymentType,
    paymentMethodId: formData.paymentMethodId,
  };
  return Promise.resolve({ paymentInformation });
};

export const proccessPaymentFormData = ({
  formData,
}: {
  formData: OnFormIsValidOptions;
}): Promise<Payment> => {
  switch (formData?.type) {
    case OnFormIsValidType.NEW_CARD:
      return processNewCard(formData);
    case OnFormIsValidType.SAVED_PAYMENT_METHOD:
      return processSavedPayment(formData);
    case OnFormIsValidType.NEW_BANK:
      return processNewBank(formData);
    default:
      return Promise.resolve({ error: 'No payment type' });
  }
};
